import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAttributeFormBuilder, useFormBuilderAttributes, useFetchedValues } from '../../../../Hooks/Display/useComponentProperties';
import { DropDownList } from '@progress/kendo-react-dropdowns';
import { useTranslation } from 'react-i18next';
import { typeText } from '../../../Attributes/Fields/AttributeTypeField';
import { Checkbox, NumericTextBox, Switch } from '@progress/kendo-react-inputs';
import { usePortalPages } from '../../../../Hooks/Display/usePages';
import { useSelectedComponent } from '../../../../Hooks/Display/useComponents';
import { useSelectedPage } from '../../../../Hooks/Display/usePages';

const EditColumnPanel = () => {
    const { t } = useTranslation(['display/components/form_builder']);
    const [{ showEditColumnPanel, editingColumn }, dispatch] = useAttributeFormBuilder();
    const [{ attributes }] = useFormBuilderAttributes();
    const [fetchedValues] = useFetchedValues();
    const portalPages = usePortalPages();
    const selectedComponent = useSelectedComponent();
    const selectedPage = useSelectedPage();
    const isNewRecordPage = selectedPage?.subPageType === "New Record";

    // Local state for form fields
    const [fontSize, setFontSize] = useState(14);
    const [fontWeight, setFontWeight] = useState({ text: 'regular', value: 'regular' });
    const [displayType, setDisplayType] = useState({ text: 'inherit', value: 'Inherit' });
    const [placeholder, setPlaceholder] = useState('');
    const [segmentColor, setSegmentColor] = useState([]);
    const [hyperlink, setHyperlink] = useState(false);
    const [targetPageId, setTargetPageId] = useState('');
    const [showLabel, setShowLabel] = useState(true);
    const [cellRules, setCellRules] = useState(false);
    const [actionReset, setActionReset] = useState(false);
    const [actionComment, setActionComment] = useState(false);
    const [actionHistory, setActionHistory] = useState(false);
    const [actionAttachment, setActionAttachment] = useState(false);

    // State to track original values for comparison
    const [originalValues, setOriginalValues] = useState({});

    // Get single record pages for hyperlink functionality
    const singleRecordPages = useMemo(() => {
        const { attributeTable: { referenceAttributePages } } = fetchedValues;
        const targetPages = referenceAttributePages && referenceAttributePages.length > 0
            ? portalPages.concat(referenceAttributePages)
            : portalPages;
        return targetPages.filter(page => page.subPageType === "Single Record");
    }, [portalPages, fetchedValues]);

    // Check if hyperlink functionality should be available for this component type and attribute type
    const isHyperlinkAvailable = useMemo(() => {
        if (!selectedComponent || !["Cards", "View"].includes(selectedComponent.componentType)) {
            return false;
        }

        // Get the current attribute being edited
        const attribute = attributes.find((attr) => attr._id === editingColumn?.attributeId);
        if (!attribute) {
            return false;
        }

        // Exclude Image and Html attribute types from hyperlink functionality
        return !["Image", "Html"].includes(attribute.attributeType);
    }, [selectedComponent, attributes, editingColumn]);

    // Check if showLabel functionality should be available for this component type
    const isShowLabelAvailable = useMemo(() => {
        return selectedComponent && selectedComponent.componentType === "Cards";
    }, [selectedComponent]);

    // Font weight options
    const fontWeightOptions = [
        { text: t('font_options.regular'), value: 'regular' },
        { text: t('font_options.bold'), value: 'bold' }
    ];

    // Display type options
    const displayTypeOptions = [
        { text: t('display_options.inherit'), value: 'Inherit' },
        { text: t('display_options.segmentedcontrol'), value: 'SegmentedControl' }
    ];


    // Helper function to create displayType object from string value
    const createDisplayTypeObject = useCallback((value) => {
        return { text: t(`display_options.${value.toLowerCase().replace(' ', '_')}`), value };
    }, [t]);

    // Function to check if there are any hex color validation errors
    const hasHexColorErrors = () => {
        if (['Picklist String', 'Mirror'].includes(attribute?.attributeType) && displayType.value === 'SegmentedControl') {
            return segmentColor.some(color =>
                (color.textColor && !validateHexColor(color.textColor)) ||
                (color.fillColor && !validateHexColor(color.fillColor))
            );
        }
        return false;
    };

    // Function to check if required hex colors are missing
    const hasMissingHexColors = () => {
        if (['Picklist String', 'Mirror'].includes(attribute?.attributeType) && displayType.value === 'SegmentedControl') {
            const dropdownValues = attribute.attributeType === 'Mirror'
                ? attribute?.attribute?.picklist?.split('ɖ') || []
                : attribute.picklist?.split('ɖ') || [];

            // Check if any dropdown value doesn't have both textColor and fillColor
            return dropdownValues.some((_, index) => {
                const color = segmentColor[index];
                return !color || !color.textColor || !color.fillColor;
            });
        }
        return false;
    };

    // Function to check if there are unsaved changes
    const hasUnsavedChanges = () => {
        if (!originalValues || Object.keys(originalValues).length === 0) {
            return false;
        }

        // If there are no existing columnSettings, user can always save (default values)
        if (!editingColumn?.columnSettings) {
            return true;
        }

        // Check displayType, fontSize, fontWeight for all attribute types
        const originalDisplayType = typeof originalValues.displayType === 'string'
            ? originalValues.displayType
            : originalValues.displayType?.value || 'Inherit';
        const displayTypeChanged = displayType.value !== originalDisplayType;
        const fontSizeChanged = fontSize !== originalValues.fontSize;
        const fontWeightChanged = fontWeight.value !== originalValues.fontWeight;

        // Check hyperlink changes only for Cards and View components
        const hyperlinkChanged = isHyperlinkAvailable ? hyperlink !== originalValues.hyperlink : false;
        const targetPageIdChanged = isHyperlinkAvailable ? targetPageId !== originalValues.targetPageId : false;

        // Check showLabel changes only for Cards components
        const showLabelChanged = isShowLabelAvailable ? showLabel !== originalValues.showLabel : false;

        // Check cellRules changes for all component types
        const cellRulesChanged = cellRules !== originalValues.cellRules;

        // Check field action changes for all component types
        const actionResetChanged = actionReset !== originalValues.actionReset;
        const actionCommentChanged = actionComment !== originalValues.actionComment;
        const actionHistoryChanged = actionHistory !== originalValues.actionHistory;
        const actionAttachmentChanged = actionAttachment !== originalValues.actionAttachment;
        const fieldActionsChanged = actionResetChanged || actionCommentChanged || actionHistoryChanged || actionAttachmentChanged;

        // Compare current values with original values based on attribute type
        if (['Picklist String', 'Mirror'].includes(originalValues.attributeType)) {
            // Deep comparison for segmentColor arrays
            const currentColorsStr = JSON.stringify(segmentColor || []);
            const originalColorsStr = JSON.stringify(originalValues.segmentColor || []);
            return (
                displayTypeChanged ||
                currentColorsStr !== originalColorsStr ||
                fontSizeChanged ||
                fontWeightChanged ||
                hyperlinkChanged ||
                targetPageIdChanged ||
                showLabelChanged ||
                cellRulesChanged ||
                fieldActionsChanged
            );
        } else if (originalValues.attributeType === 'Multiline Text') {
            return (
                displayTypeChanged ||
                placeholder !== originalValues.placeholder ||
                fontSizeChanged ||
                fontWeightChanged ||
                hyperlinkChanged ||
                targetPageIdChanged ||
                showLabelChanged ||
                cellRulesChanged ||
                fieldActionsChanged
            );
        } else {
            // Handle String, Number, DateTime and any other attribute types
            return (
                displayTypeChanged ||
                fontSizeChanged ||
                fontWeightChanged ||
                hyperlinkChanged ||
                targetPageIdChanged ||
                showLabelChanged ||
                cellRulesChanged ||
                fieldActionsChanged
            );
        }
    };

    // Initialize form values when attribute changes
    useEffect(() => {
        if (editingColumn && attributes.length > 0) {
            const attribute = attributes.find((attr) => attr._id === editingColumn.attributeId);
            if (attribute) {
                // Load existing column settings if they exist
                if (editingColumn.columnSettings) {
                    const settings = editingColumn.columnSettings;

                    // Set fontSize and fontWeight for all attribute types
                    setFontSize(settings.fontSize || 14);

                    // Handle both old object format and new string format for fontWeight
                    if (settings.fontWeight) {
                        if (typeof settings.fontWeight === 'string') {
                            setFontWeight({ text: t(`font_options.${settings.fontWeight.toLowerCase()}`), value: settings.fontWeight });
                        } else if (settings.fontWeight.value) {
                            setFontWeight(settings.fontWeight);
                        }
                    } else {
                        setFontWeight({ text: t('font_options.regular'), value: 'regular' });
                    }

                    if (['String', 'Number', 'DateTime'].includes(attribute.attributeType)) {
                        let displayTypeValue = 'Inherit';
                        if (settings.displayType) {
                            if (typeof settings.displayType === 'string') {
                                displayTypeValue = settings.displayType;
                            } else if (settings.displayType.value) {
                                displayTypeValue = settings.displayType.value;
                            }
                        }
                        setDisplayType(createDisplayTypeObject(displayTypeValue));
                    } else if (['Picklist String', 'Mirror'].includes(attribute.attributeType)) {
                        // Handle both old object format and new string format
                        let displayTypeValue = 'Inherit';
                        if (settings.displayType) {
                            if (typeof settings.displayType === 'string') {
                                displayTypeValue = settings.displayType;
                            } else if (settings.displayType.value) {
                                displayTypeValue = settings.displayType.value;
                            }
                        }
                        setDisplayType(createDisplayTypeObject(displayTypeValue));

                        // Handle both old format (TextColor/FillColor) and new format (textColor/fillColor)
                        if (settings.segmentColor && Array.isArray(settings.segmentColor)) {
                            const normalizedColors = settings.segmentColor.map(color => {
                                if (color.TextColor !== undefined || color.FillColor !== undefined) {
                                    // Convert old format to new format
                                    return {
                                        textColor: color.TextColor || color.textColor || '',
                                        fillColor: color.FillColor || color.fillColor || ''
                                    };
                                }
                                return color; // Already in new format
                            });
                            setSegmentColor(normalizedColors);
                        } else {
                            setSegmentColor([]);
                        }
                    } else if (attribute.attributeType === 'Multiline Text') {
                        let displayTypeValue = 'Inherit';
                        if (settings.displayType) {
                            if (typeof settings.displayType === 'string') {
                                displayTypeValue = settings.displayType;
                            } else if (settings.displayType.value) {
                                displayTypeValue = settings.displayType.value;
                            }
                        }
                        setDisplayType(createDisplayTypeObject(displayTypeValue));
                        setPlaceholder(settings.placeholder || '');
                    }
                    // Set hyperlink and targetPageId only for Cards and View components
                    if (isHyperlinkAvailable) {
                        setHyperlink(Boolean(settings.hyperlink));
                        setTargetPageId(settings.targetPageId || '');
                    }

                    // Set showLabel only for Cards components
                    if (isShowLabelAvailable) {
                        setShowLabel(Boolean(settings.showLabel !== undefined ? settings.showLabel : true));
                    }

                    // Set cellRules for all component types
                    setCellRules(Boolean(settings.cellRules !== undefined ? settings.cellRules : false));

                    // Set field actions for all component types
                    setActionReset(Boolean(settings.actionReset));
                    setActionComment(Boolean(settings.actionComment));
                    setActionHistory(Boolean(settings.actionHistory));
                    setActionAttachment(Boolean(settings.actionAttachment));
                } else {
                    // Set default values for all attribute types
                    setFontSize(14);
                    setFontWeight({ text: t('font_options.regular'), value: 'regular' });

                    // Set hyperlink defaults only for Cards and View components
                    if (isHyperlinkAvailable) {
                        setHyperlink(false);
                        setTargetPageId('');
                    }

                    // Set showLabel defaults only for Cards components
                    if (isShowLabelAvailable) {
                        setShowLabel(true);
                    }

                    // Set cellRules defaults for all component types
                    setCellRules(false);

                    // Set field action defaults for all component types
                    setActionReset(false);
                    setActionComment(false);
                    setActionHistory(false);
                    setActionAttachment(false);

                    // Set specific defaults based on property type
                    if (['String', 'Number', 'DateTime'].includes(attribute.attributeType)) {
                        setDisplayType(createDisplayTypeObject('Inherit'));
                    } else if (['Picklist String', 'Mirror'].includes(attribute.attributeType)) {
                        setDisplayType(createDisplayTypeObject('Inherit'));
                    } else if (attribute.attributeType === 'Multiline Text') {
                        setDisplayType(createDisplayTypeObject('Inherit'));
                        setPlaceholder('');
                    }
                }

                // Store original values for comparison
                const originalData = {
                    attributeType: attribute.attributeType,
                    displayType: editingColumn.columnSettings?.displayType || { text: t('display_options.inherit'), value: 'Inherit' },
                    fontSize: editingColumn.columnSettings?.fontSize || 14,
                    fontWeight: editingColumn.columnSettings?.fontWeight || 'regular',
                    placeholder: editingColumn.columnSettings?.placeholder || '',
                    segmentColor: editingColumn.columnSettings?.segmentColor || []
                };

                // Only include hyperlink values for Cards and View components
                if (isHyperlinkAvailable) {
                    originalData.hyperlink = Boolean(editingColumn.columnSettings?.hyperlink);
                    originalData.targetPageId = editingColumn.columnSettings?.targetPageId || '';
                }

                // Only include showLabel values for Cards components
                if (isShowLabelAvailable) {
                    originalData.showLabel = Boolean(editingColumn.columnSettings?.showLabel !== undefined ? editingColumn.columnSettings?.showLabel : true);
                }

                // Include cellRules values for all component types
                originalData.cellRules = Boolean(editingColumn.columnSettings?.cellRules !== undefined ? editingColumn.columnSettings?.cellRules : false);

                // Include field actions for all component types
                originalData.actionReset = Boolean(editingColumn.columnSettings?.actionReset);
                originalData.actionComment = Boolean(editingColumn.columnSettings?.actionComment);
                originalData.actionHistory = Boolean(editingColumn.columnSettings?.actionHistory);
                originalData.actionAttachment = Boolean(editingColumn.columnSettings?.actionAttachment);
                setOriginalValues(originalData);
            }
        }
    }, [editingColumn, attributes, t, createDisplayTypeObject, isHyperlinkAvailable, isShowLabelAvailable]);

    if (!showEditColumnPanel || !editingColumn) {
        return null;
    }

    const attribute = attributes.find((attr) => attr._id === editingColumn.attributeId);
    let dropdownValues = [];
    if (attribute.attributeType === 'Mirror') {
        dropdownValues = attribute?.attribute?.picklist?.split('ɖ') || [];
    } else {
        dropdownValues = attribute.picklist?.split('ɖ') || [];
    }

    if (!attribute) return null;

    const handleClose = () => {
        dispatch.toggleEditColumnPanel();
        dispatch.setEditingColumn(null);
    };

    const handleSave = () => {
        // Create columnSettings object based on attribute type
        let columnSettings = {
            displayType: displayType.value,
            fontSize: fontSize,
            fontWeight: fontWeight.value,
            placeholder: "",
            segmentColor: [],
            cellRules: cellRules,
            actionReset: actionReset,
            actionComment: actionComment,
            actionHistory: actionHistory,
            actionAttachment: actionAttachment
        };

        // Only include hyperlink settings for Cards and View components
        if (isHyperlinkAvailable) {
            columnSettings.hyperlink = hyperlink;
            columnSettings.targetPageId = targetPageId;
        }

        // Only include showLabel settings for Cards components
        if (isShowLabelAvailable) {
            columnSettings.showLabel = showLabel;
        }

        if (['Picklist String', 'Mirror'].includes(attribute.attributeType)) {
            columnSettings.displayType = displayType.value;
            columnSettings.segmentColor = segmentColor;
        } else if (attribute.attributeType === 'Multiline Text') {
            columnSettings.placeholder = placeholder;
        }

        // Save the column settings
        dispatch.updateColumnSettings(editingColumn.attributeId, columnSettings);

        handleClose();
    };

    const validateHexColor = (color) => {
        const hexRegex = /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/;
        return hexRegex.test(color);
    };

    const handleColorChange = (index, field, colorType) => {
        setSegmentColor(prev => {
            const newColors = [...prev];
            if (!newColors[index]) {
                newColors[index] = { textColor: '', fillColor: '' };
            }
            newColors[index] = {
                ...newColors[index],
                [colorType === 'textColor' ? 'textColor' : 'fillColor']: field
            };
            return newColors;
        });
    };

    const handleHyperlinkToggle = (event) => {
        const isEnabled = event.value
        setHyperlink(isEnabled);
        if (isEnabled) {
            // Auto-select target page based on attribute's entity
            const targetEntityId = attribute.attributeType === "Entity Lookup" ? attribute.referencedEntityId : attribute.entityId;
            const targetPage = singleRecordPages.find(page => page.entityId === targetEntityId);
            setTargetPageId(targetPage ? targetPage._id : '');
        } else {
            setTargetPageId('');
        }
    };


    const renderDropdownFields = () => (
        <>
            {displayType.value === 'SegmentedControl' && (
                <div className="mb-1">
                    <label className="form-label">{t('column_settings.colors')}</label>
                    <div className="border rounded p-3">
                        <div className="row g-2">
                            <div className="col-6">
                                <span>{t('column_settings.text_color')}</span>
                            </div>
                            <div className="col-6">
                                <span>{t('column_settings.fill_color')}</span>
                            </div>
                        </div>
                        {dropdownValues.map((value, index) => (
                            <div key={index} className="mb-2">
                                <div className="row g-2">
                                    <div className="col-6">
                                        <input
                                            type="text"
                                            className="form-control form-control-sm"
                                            placeholder={t('color_inputs.text_color_placeholder')}
                                            value={segmentColor[index]?.textColor || ''}
                                            onChange={(e) => handleColorChange(index, e.target.value, 'textColor')}
                                            style={{
                                                borderColor: segmentColor[index]?.textColor && !validateHexColor(segmentColor[index].textColor) ? 'red' : ''
                                            }}
                                        />
                                        {segmentColor[index]?.textColor && !validateHexColor(segmentColor[index].textColor) && (
                                            <small className="text-danger">{t('color_inputs.color_code_error')}</small>
                                        )}
                                    </div>
                                    <div className="col-6">
                                        <input
                                            type="text"
                                            className="form-control form-control-sm"
                                            placeholder={t('color_inputs.background_placeholder')}
                                            value={segmentColor[index]?.fillColor || ''}
                                            onChange={(e) => handleColorChange(index, e.target.value, 'backgroundColor')}
                                            style={{
                                                borderColor: segmentColor[index]?.fillColor && !validateHexColor(segmentColor[index].fillColor) ? 'red' : ''
                                            }}
                                        />
                                        {segmentColor[index]?.fillColor && !validateHexColor(segmentColor[index].fillColor) && (
                                            <small className="text-danger">{t('color_inputs.color_code_error')}</small>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </>
    );

    const renderLongTextField = () => (
        <div className="mb-1">
            <label className="form-label">{t('column_settings.placeholder')}</label>
            <input
                type="text"
                className="form-control"
                value={placeholder}
                onChange={(e) => setPlaceholder(e.target.value)}
                maxLength={64}
                style={{ width: '100%' }}
            />
            <small className="text-muted">{placeholder.length}/64 {t('column_settings.characters_count')}</small>
        </div>
    );

    const renderFieldsByType = () => {
        if (['Picklist String', 'Mirror'].includes(attribute.attributeType)) {
            return renderDropdownFields();
        } else if (attribute.attributeType === 'Multiline Text') {
            return renderLongTextField();
        }
        return null;
    };

    return (
        <div
            className="edit-column-panel position-fixed end-0 top-0 h-100 bg-white border-start shadow-lg"
            style={{ width: '400px', zIndex: 1050000 }}>
            <div className="d-flex flex-column h-100">
                {/* Header */}
                <div className="d-flex justify-content-between align-items-center p-3 border-bottom">
                    <h5 className="mb-0">{attribute.attributeLabel} - {t('edit_column_panel.title')}</h5>
                    <button
                        type="button"
                        className="btn-close"
                        onClick={handleClose}
                        aria-label={t('edit_column_panel.buttons.cancel')}
                    ></button>
                </div>

                {/* Content */}
                <div className="flex-grow-1 p-3 overflow-auto">
                    <div>
                        <label className="form-label">{t('edit_column_panel.type_label')}</label>
                        <div className="form-control-plaintext">
                            {typeText[attribute.attributeType]}
                        </div>
                    </div>

                    {/* Font Size and Weight - Available for all attribute types */}
                    <div className="mb-1">
                        <label className="form-label">{t('column_settings.font_size')} (px)</label>
                        <NumericTextBox
                            min={1}
                            max={72}
                            value={fontSize}
                            onChange={(e) => setFontSize(e.value)}
                            style={{ width: '100%' }}
                        />
                    </div>

                    <div className="mb-1">
                        <label className="form-label">{t('column_settings.font_weight')}</label>
                        <DropDownList
                            data={fontWeightOptions}
                            value={fontWeight}
                            onChange={(e) => setFontWeight(e.value)}
                            textField="text"
                            dataItemKey="value"
                            style={{ width: '100%' }}
                        />
                    </div>

                    {/* Show Label - Available for Cards components only */}
                    {isShowLabelAvailable && (
                        <div className="mb-1">
                            <label className="form-label">
                                {t('column_settings.show_label')}
                            </label>
                            <br />
                            <Checkbox
                                checked={showLabel}
                                onChange={(e) => setShowLabel(e.value)}
                            />
                        </div>
                    )}
                    {/* Show Cell Formatting - Available for all component types */}
                    {/* <div className="mb-1">
                        <label className="form-label">Show Cell Formatting</label>
                        <br />
                        <Checkbox
                            checked={cellRules}
                            onChange={(e) => setCellRules(e.value)}
                        />
                    </div> */}

                    {/* Hyperlink Settings - Available for Cards and View components only */}
                    {isHyperlinkAvailable && (
                        <>

                            <div className="mb-1">
                                <label className="form-label mb-0">{t('column_settings.hyperlink')}</label>
                                <br />
                                <Switch
                                    checked={hyperlink}
                                    onChange={handleHyperlinkToggle}
                                    disabled={singleRecordPages.length === 0}
                                />
                            </div>
                            {targetPageId && (
                                <div className="mb-1">
                                    <label className="form-label mb-0">{t('column_settings.target_page')}</label>
                                    <br />
                                    <div className="form-control-plaintext">

                                        <p className="mb-0">
                                            <span className="text-muted">
                                                {singleRecordPages.find(page => page._id === targetPageId)?.pageName || t('column_settings.target_page_not_found')}
                                            </span>
                                        </p>
                                    </div>
                                </div>
                            )}
                        </>
                    )}

                    {/* Field Actions - Available for all attribute types */}
                    <div className="mb-1">
                        <label className="form-label">{t('field_actions.title')}</label>
                        <div className="border rounded p-2 d-flex flex-wrap gap-3">
                            <Checkbox
                                checked={actionReset}
                                onChange={(e) => setActionReset(e.value)}
                                label={t('field_actions.reset')}
                            />
                            <Checkbox
                                checked={actionComment}
                                onChange={(e) => setActionComment(e.value)}
                                label={t('field_actions.comment')}
                            />
                            {!isNewRecordPage && (
                                <Checkbox
                                    checked={actionHistory}
                                    onChange={(e) => setActionHistory(e.value)}
                                    label={t('field_actions.history')}
                                />
                            )}
                            <Checkbox
                                checked={actionAttachment}
                                onChange={(e) => setActionAttachment(e.value)}
                                label={t('field_actions.attachment')}
                            />
                        </div>
                    </div>

                    {/* Display Type - Available for all attribute types */}
                    <div className="mb-1">
                        <label className="form-label">{t('column_settings.display_type')}</label>
                        <DropDownList
                            data={displayTypeOptions.filter(option =>
                                option.value === 'Inherit' ||
                                (option.value === 'SegmentedControl' && ['Picklist String', 'Mirror'].includes(attribute.attributeType))
                            )}
                            value={displayType}
                            onChange={(e) => setDisplayType(e.value)}
                            textField="text"
                            dataItemKey="value"
                            style={{ width: '100%' }}
                        />
                    </div>

                    {renderFieldsByType()}
                </div>

                {/* Footer with Buttons */}
                <div className="p-3 border-top">
                    <div className="d-flex gap-2 justify-content-end">
                        <button
                            type="button"
                            className="btn btn-secondary"
                            onClick={handleClose}
                        >
                            {t('edit_column_panel.buttons.cancel')}
                        </button>
                        <button
                            type="button"
                            className="btn btn-primary"
                            onClick={handleSave}
                            disabled={!hasUnsavedChanges() || hasHexColorErrors() || hasMissingHexColors()}
                        >
                            {t('edit_column_panel.buttons.save')}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default EditColumnPanel;